import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'

import styles from './AppointmentsDisabilitiesController.module.sass'
import { useFetch } from 'store'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { AppointmentConnectionDisability } from 'models'
import AppointmentsTable from './AppointmentsTable'
import PathsDiagram from './PathsDiagram'
import { useForm, FormProvider } from 'react-hook-form'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import EditConnectionModal from './EditConnectionModal'

const AppointmentsDisabilitiesController = () => {
	const { i18n } = useGlobal()
	const history = useHistory()
	const params = useParams()
	const { clinicalProcessId } = params
	const location = useLocation()
	const { search } = location
	const [refreshKey, setRefreshKey] = useState(0)

	const basePath = `/clinical-processes/${clinicalProcessId}/disabilities`

	const { state, contents } = useFetch(
		`/clinical_processes/${clinicalProcessId}/disabilities-connections?refresh=${refreshKey}`,
		AppointmentConnectionDisability.fromJSON
	)

	const isLoading = state === 'loading'
	const containerRef = useRef(null)
	const methods = useForm()
	const selectedId = useMemo(() => {
		const searchParams = new URLSearchParams(search)

		return searchParams.get('origin')
	}, [search])

	const [showAll, setShowAll] = useState(false)
	const [editingAppointment, setEditingAppointment] = useState(null)

	const handleCloseEditModal = (updated) => {
		setEditingAppointment(null)
		if (updated) {
			setRefreshKey((k) => k + 1)
		}
	}
	const selectedPathIds = useMemo(() => {
		if (!selectedId || !Array.isArray(contents)) return []
		for (const item of contents) {
			const matchedPath = item.paths.find((path) =>
				path.some(
					(appointment) =>
						String(appointment.id) === String(selectedId)
				)
			)
			if (matchedPath) {
				return matchedPath.map((a) => String(a.id))
			}
		}
		return [selectedId]
	}, [selectedId, contents])
	const handleRowClick = (id) => {
		if (selectedId === id) {
			history.replace(basePath)
		} else {
			history.push(`${basePath}?origin=${id}`)
		}
	}

	useEffect(() => {
		const handleClick = (event) => {
			if (event.target.closest('tr')) return
			if (event.target.closest('svg')) return
			if (event.target.tagName === 'INPUT') return
			if (event.target.tagName === 'LABEL') return

			if (
				event.target.dataset.id !== undefined &&
				selectedId !== event.target?.dataset?.id
			) {
				history.push(`${basePath}?origin=${event.target.dataset.id}`)
			} else {
				history.replace(basePath)
			}
		}
		document.body.addEventListener('click', handleClick, true)
		return () => {
			document.body.removeEventListener('click', handleClick, true)
		}
	}, [basePath, history, selectedId])

	return (
		<div ref={containerRef}>
			{isLoading ? (
				<h4>{i18n`Loading`}...</h4>
			) : (
				(contents ?? []).map((item, idx) => (
					<>
						<div className={styles['episode-header']}>
							<h2>
								{i18n`Clinical episode`} {idx + 1} /{' '}
								{contents.length}
							</h2>

							<div className={styles['episode-actions']}>
								{idx === 0 && (
									<label className={styles['show-all-label']}>
										<input
											type='checkbox'
											checked={showAll}
											onChange={(e) =>
												setShowAll(e.target.checked)
											}
										/>
										{i18n`Show all appointments`}
									</label>
								)}

								{idx === 0 && (
									<button
										type='button'
										className={styles['check-disabilities']}
										onClick={() =>
											setEditingAppointment({
												tableData: item.tableData,
												finalAppointments:
													item.finalAppointments,
											})
										}
									>
										<Icon
											name='branch'
											width='20px'
											height='20px'
										/>
										{i18n`Edit Connection`}
									</button>
								)}
							</div>
						</div>

						<div className={styles['table-container']}>
							<PathsDiagram
								paths={showAll ? item.allPaths : item.paths}
								pointsMapper={
									showAll
										? item.allPointsMapper
										: item.pointsMapper
								}
								pathColumns={item.pathColumns}
								selectedId={selectedId}
								onRowClick={handleRowClick}
							/>

							<FormProvider {...methods}>
								<AppointmentsTable
									data={
										showAll
											? item.allTableData
											: item.tableData
									}
									selectedId={selectedId}
									selectedPathIds={selectedPathIds}
									onRowClick={handleRowClick}
									onEditAppointment={(appointmentId) =>
										setEditingAppointment({
											tableData: item.tableData,
											finalAppointments:
												item.finalAppointments,
											initialAppointmentId: appointmentId,
										})
									}
								/>
							</FormProvider>
						</div>
						{editingAppointment && (
							<EditConnectionModal
								onClose={handleCloseEditModal}
								tableData={editingAppointment.tableData}
								finalAppointments={
									editingAppointment.finalAppointments
								}
								initialAppointmentId={
									editingAppointment.initialAppointmentId
								}
							/>
						)}
					</>
				))
			)}

			<div className={styles.legendContainer}>
				<h4 className={styles.legendTitle}>{i18n`Legend`}</h4>

				<div className={styles.legendItems}>
					<div className={styles.legendItem}>
						<span
							className={`${styles.dot} ${styles.dotBlue}`}
						></span>
						<span>{i18n`First Appointment`}</span>
					</div>

					<div className={styles.legendItem}>
						<span
							className={`${styles.dot} ${styles.dotOrange}`}
						></span>
						<span>{i18n`Intermediate Appointment`}</span>
					</div>

					<div className={styles.legendItem}>
						<span
							className={`${styles.dot} ${styles.dotRed}`}
						></span>
						<span>{i18n`Last Appointment`}</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default AppointmentsDisabilitiesController
